import { useEffect, useMemo, useRef, useState } from 'react'
import categories from '../categories'
import { institutionalColors } from '~/design'
import styled from 'styled-components'
import UpIcon from '~/components/icons/arrows/16px_minimal-up.svg'
import DownIcon from '~/components/icons/arrows/16px_minimal-down.svg'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons'
import { useMedia } from 'react-use'

type CategoryInfo = {
  key: string
  title: string
  sectorKey: string
  parentKey?: string
  childrenKeys?: string[]
  defaultChildKey?: string
}

interface CategoryButtonsProps {
  categoryKey: string
  categoryName: string
  selectedCategory: typeof categories
  categoriesInfo: CategoryInfo[]
  handleSelectCategory: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    info: {
      key: string
      title: string
      sectorKey: string
    },
  ) => void
  isModalOpen?: boolean
  highlightedCategoryKeys?: string[]
  hasHighlight?: boolean
}

const TitleContainer = styled.div`
  display: flex;
  gap: 4px;
  place-items: center;
  cursor: pointer;
  transition-duration: 0.3s;

  h5 {
    font-weight: 500;
    font-size: 0.8rem;
    text-transform: uppercase;
    margin: 1rem 0;
    color: ${({ theme }) => theme.colors.darkGrey};
    user-select: none;
    letter-spacing: 1px;
  }

  svg {
    fill: ${institutionalColors.graySebrae30};
    transition: transform 0.2s ease-in-out;
  }
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  margin: 0 2px;
  overflow: hidden;
`

interface CategoryListProps {
  categoryKey: string
  selectedCategory: typeof categories
  categoriesInfo: CategoryInfo[]
  handleSelectCategory: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    info: { key: string; title: string; sectorKey: string },
  ) => void
  onContentChange?: () => void
  highlightedCategoryKeys?: string[]
}

const CategoryList = ({
  categoryKey,
  selectedCategory,
  categoriesInfo,
  handleSelectCategory,
  onContentChange,
  highlightedCategoryKeys,
}: CategoryListProps) => {
  const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({})
  const selectedKey = Object.keys(selectedCategory)[0]
  const categoriesMap = useMemo(() => {
    const map: Record<string, CategoryInfo> = {}
    categoriesInfo.forEach(info => {
      map[info.key] = info
    })
    return map
  }, [categoriesInfo])

  useEffect(() => {
    const selectedInfo = categoriesMap[selectedKey]
    if (selectedInfo?.parentKey) {
      setExpandedParents({ [selectedInfo.parentKey!]: true })
    } else {
      setExpandedParents({})
    }
  }, [categoriesMap, selectedKey])

  useEffect(() => {
    onContentChange?.()
  }, [expandedParents, onContentChange])

  const toggleNested = (event: React.MouseEvent, info: CategoryInfo) => {
    event.preventDefault()
    event.stopPropagation()
    const children = getChildren(info)
    if (children.length > 0) {
      const isCurrentlyExpanded = expandedParents[info.key]
      if (isCurrentlyExpanded) {
        setExpandedParents(prev => ({
          ...prev,
          [info.key]: false,
        }))
      } else {
        setExpandedParents(prev => ({
          ...prev,
          [info.key]: true,
        }))
        const childToSelect =
          (info.defaultChildKey
            ? children.find(child => child.key === info.defaultChildKey)
            : children[0]) || children[0]
        if (childToSelect) {
          handleSelectCategory(
            event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
            childToSelect,
          )
        }
      }
    } else {
      handleSelectCategory(
        event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>,
        info,
      )
    }
  }

  const getChildren = (info: CategoryInfo) =>
    categoriesInfo.filter(child => child.parentKey === info.key)

  const parentHasChildren = (info: CategoryInfo) => getChildren(info).length > 0
  const parentCategoryInfo = categoriesMap[categoryKey]
  const parentHasDirectChildren = parentCategoryInfo
    ? categoriesInfo.some(info => info.parentKey === parentCategoryInfo.key)
    : false

  const topLevelCategories = parentHasDirectChildren
    ? getChildren(parentCategoryInfo!)
    : categoriesInfo
        .filter(
          info =>
            info.sectorKey === categoryKey &&
            (!info.parentKey || categoriesMap[info.parentKey]?.sectorKey !== categoryKey),
        )
        .sort((a, b) => {
          const aHasChildren = parentHasChildren(a)
          const bHasChildren = parentHasChildren(b)
          if (aHasChildren === bHasChildren) {
            return 0
          }
          return aHasChildren ? 1 : -1
        })

  return (
    <>
      {topLevelCategories.map(info => {
        const children = getChildren(info)
        const hasChildren = children.length > 0
        const isHighlighted = highlightedCategoryKeys?.includes(info.key) ?? false

        return (
          <div className='category-item-wrapper' key={`category-${info.key}`}>
            <button
              className={`${
                selectedKey === info.key ? 'button-active-category' : ''
              } ${hasChildren ? 'has-children' : ''}`}
              onClick={event => {
                if (hasChildren) {
                  toggleNested(event, info)
                } else {
                  handleSelectCategory(event, info)
                }
              }}
            >
              <span>
                {isHighlighted && (
                  <FontAwesomeIcon
                    icon={faStar}
                    style={{ marginRight: '6px', color: '#0C2340', fontSize: '0.8rem' }}
                  />
                )}
                {info.title}
              </span>
              {hasChildren && (
                <span className='nested-toggle' onClick={event => toggleNested(event, info)}>
                  {expandedParents[info.key] ? (
                    <UpIcon width={10} height={10} />
                  ) : (
                    <DownIcon width={10} height={10} />
                  )}
                </span>
              )}
            </button>
            {hasChildren && expandedParents[info.key] && (
              <div className='nested-category-list'>
                {children.map(child => {
                  const isChildHighlighted = highlightedCategoryKeys?.includes(child.key) ?? false
                  return (
                    <button
                      key={`category-${child.key}`}
                      className={`nested-category-button ${
                        selectedKey === child.key ? 'button-active-category' : ''
                      }`}
                      onClick={event => handleSelectCategory(event, child)}
                    >
                      <div>
                        {isChildHighlighted && (
                          <FontAwesomeIcon
                            icon={faStar}
                            style={{ marginRight: '6px', color: '#0C2340', fontSize: '0.75rem' }}
                          />
                        )}
                        {child.title}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

export function CategoryButtons({
  categoryKey,
  categoryName,
  selectedCategory,
  categoriesInfo,
  handleSelectCategory,
  isModalOpen,
  highlightedCategoryKeys,
}: CategoryButtonsProps) {
  const hasHighlightedChildren = highlightedCategoryKeys?.includes(categoryKey)
  return (
    <>
      <h5>
        {hasHighlightedChildren && (
          <FontAwesomeIcon
            icon={faStar}
            style={{ marginRight: '6px', color: '#0C2340', fontSize: '0.8rem' }}
          />
        )}
        <span>{categoryName}</span>
      </h5>
      <CategoryList
        categoryKey={categoryKey}
        selectedCategory={selectedCategory}
        categoriesInfo={categoriesInfo}
        handleSelectCategory={handleSelectCategory}
        highlightedCategoryKeys={highlightedCategoryKeys}
      />
    </>
  )
}

export function CategoryButtonsDropdown({
  categoryKey,
  categoryName,
  selectedCategory,
  categoriesInfo,
  handleSelectCategory,
  isModalOpen,
  highlightedCategoryKeys,
}: CategoryButtonsProps) {
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const isMobile = useMedia('(max-width: 768px)', false)
  const prevModalOpenRef = useRef(false)

  useEffect(() => {
    if (sectionRef.current) {
      sectionRef.current.style.maxHeight = isCategoryMenuOpen
        ? `${sectionRef.current.scrollHeight}px`
        : '0'
    }
  }, [isCategoryMenuOpen])

  useEffect(() => {
    if (isModalOpen && !prevModalOpenRef.current) {
      const selectedKey = Object.keys(selectedCategory)[0]
      const selectedInfo = categoriesInfo.find(info => info.key === selectedKey)
      if (selectedInfo) {
        const parentInfo = selectedInfo.parentKey
          ? categoriesInfo.find(info => info.key === selectedInfo.parentKey)
          : undefined
        const belongsToCategory =
          selectedInfo.sectorKey === categoryKey ||
          parentInfo?.sectorKey === categoryKey ||
          selectedInfo.parentKey === categoryKey
        if (belongsToCategory) {
          setIsCategoryMenuOpen(true)
        }
      }
    }
    prevModalOpenRef.current = isModalOpen ?? false
  }, [isModalOpen, categoriesInfo, categoryKey, selectedCategory])

  // If the screen is mobile, we render the CategoryButtons component instead
  if (isMobile) {
    return (
      <CategoryButtons
        categoryKey={categoryKey}
        categoryName={categoryName}
        selectedCategory={selectedCategory}
        categoriesInfo={categoriesInfo}
        handleSelectCategory={handleSelectCategory}
        isModalOpen={isModalOpen}
        highlightedCategoryKeys={highlightedCategoryKeys}
      />
    )
  }

  const toggleCategoryMenu = () => {
    setIsCategoryMenuOpen(prev => !prev)
    sectionRef!.current!.style.transitionDuration = '0.3s'
  }

  const recalcHeight = () => {
    if (sectionRef.current && isCategoryMenuOpen) {
      sectionRef.current.style.maxHeight = `${sectionRef.current.scrollHeight}px`
    }
  }

  const hasHighlightedChildren = highlightedCategoryKeys?.includes(categoryKey)
  return (
    <>
      <TitleContainer onClick={() => toggleCategoryMenu()}>
        <h5>
          {hasHighlightedChildren && (
            <FontAwesomeIcon
              icon={faStar}
              style={{ marginRight: '6px', color: '#0C2340', fontSize: '0.8rem' }}
            />
          )}
          {categoryName}
        </h5>
        {isCategoryMenuOpen ? (
          <UpIcon width={10} height={10} />
        ) : (
          <DownIcon width={10} height={10} />
        )}
      </TitleContainer>

      <SectionContainer ref={sectionRef}>
        <CategoryList
          categoryKey={categoryKey}
          selectedCategory={selectedCategory}
          categoriesInfo={categoriesInfo}
          handleSelectCategory={handleSelectCategory}
          onContentChange={recalcHeight}
          highlightedCategoryKeys={highlightedCategoryKeys}
        />
      </SectionContainer>
    </>
  )
}
